import React, { useEffect, useState } from "react";
import type { Product } from "@/types";
import { fetchSuggestions } from "@/lib/api";
import { formatUzs } from "@/lib/format";
import { flyToCart } from "@/lib/motion";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useCart } from "@/state/CartContext";
import { ProductImage } from "./ProductImage";
import { toneFor } from "./ProductCard";

/** "Добавьте ещё N — доставка бесплатно". Hidden when the shop has no threshold. */
export function FreeDeliveryProgress({ subtotal, threshold }: { subtotal: number; threshold: number }) {
  const { t, locale } = useI18n();
  if (!threshold) return null;
  const left = threshold - subtotal;
  return (
    <div className="freeship">
      {left > 0 ? (
        <span>{t("cart.freeShipLeft", { amount: formatUzs(left, locale) })}</span>
      ) : (
        <span className="freeship__done">◆ {t("cart.freeShipDone")}</span>
      )}
      <div className="freeship__bar">
        <div style={{ width: `${Math.min(100, (subtotal / threshold) * 100)}%` }} />
      </div>
    </div>
  );
}

/** One-tap add-ons picked by staff for the products in the cart, then accessories. */
export function AddOnRail() {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const { lines, addItem } = useCart();
  const [items, setItems] = useState<Product[]>([]);
  const key = lines.map((l) => l.productId).sort().join(",");

  useEffect(() => {
    fetchSuggestions([...new Set(lines.map((l) => l.productId))]).then(setItems).catch(() => setItems([]));
  }, [key, locale]);

  if (items.length === 0) return null;
  return (
    <>
      <div className="section-heading" style={{ margin: "var(--space-5) 0 var(--space-3)", fontSize: 19 }}>
        <span className="diamond" />
        {t("cart.addOnsTitle")}
      </div>
      <div className="addon">
        {items.map((p) => {
          const v = p.variants.find((x) => x.stock > 0) ?? p.variants[0];
          return (
            <div key={p.id} className="addon__item">
              <button className="addon__img" style={{ background: `var(--tone-${toneFor(p.id)})` }} onClick={() => navigate({ name: "product", slug: p.slug })}>
                <ProductImage src={p.images[0]} alt={p.name} category={p.category} className="product-card__image" />
              </button>
              <div className="addon__name">{p.name}</div>
              <div className="addon__price">{formatUzs(v.priceUzs, locale)}</div>
              <button
                className="btn btn--ghost addon__btn"
                onClick={(e) => {
                  flyToCart(e.currentTarget.parentElement?.querySelector(".addon__img") ?? null);
                  addItem(p.id, v.id);
                }}
              >
                + {t("cart.add")}
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}
